import { z } from "zod";
import type { StatusResponse } from "./statuspage.js";

/** Kennisbank, changelog en volgen (docs/HELPCENTER.md §5–§7). */

export const KB_VISIBILITIES = ["public", "logged_in"] as const;
export type KbVisibility = (typeof KB_VISIBILITIES)[number];
export const KB_VISIBILITY_LABELS: Record<KbVisibility, string> = {
  public: "Iedereen",
  logged_in: "Alleen in de app",
};

export const PUBLISH_STATUSES = ["draft", "published"] as const;
export type PublishStatus = (typeof PUBLISH_STATUSES)[number];
export const PUBLISH_STATUS_LABELS: Record<PublishStatus, string> = { draft: "Concept", published: "Gepubliceerd" };

/** Startcategorieën (seed); te beheren in platformbeheer. Iconen uit Lucide. */
export const DEFAULT_KB_CATEGORIES = [
  { slug: "aan-de-slag", title: "Aan de slag", icon: "rocket", sort: 1 },
  { slug: "plattegrond-en-bevindingen", title: "Plattegrond en bevindingen", icon: "map-pin", sort: 2 },
  { slug: "taken-en-rapport", title: "Taken en rapport", icon: "clipboard-list", sort: 3 },
  { slug: "assets-en-qr-codes", title: "Assets en QR-codes", icon: "qr-code", sort: 4 },
  { slug: "dossier", title: "Dossier", icon: "folder-open", sort: 5 },
  { slug: "mjop", title: "MJOP", icon: "calendar-range", sort: 6 },
  { slug: "meterstanden", title: "Meterstanden", icon: "gauge", sort: 7 },
  { slug: "account-en-abonnement", title: "Account en abonnement", icon: "user-cog", sort: 8 },
] as const;

export const CHANGELOG_KINDS = ["new", "improved", "fixed"] as const;
export type ChangelogKind = (typeof CHANGELOG_KINDS)[number];
export const CHANGELOG_KIND_LABELS: Record<ChangelogKind, string> = {
  new: "Nieuw",
  improved: "Verbeterd",
  fixed: "Opgelost",
};

export const SUBSCRIPTION_TOPICS = ["status", "changelog"] as const;
export type SubscriptionTopic = (typeof SUBSCRIPTION_TOPICS)[number];
export const SUBSCRIPTION_TOPIC_LABELS: Record<SubscriptionTopic, string> = {
  status: "Statusmeldingen",
  changelog: "Nieuw in Klantpunt",
};

/** Kleine letters, cijfers en '-'; voor artikelen en categorieën. */
export function slugify(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80)
    .replace(/-+$/g, "");
}

// ---------- Kennisbank: API ----------

const slugSchema = z
  .string()
  .trim()
  .min(1, "Vul een slug in")
  .max(80)
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "Alleen kleine letters, cijfers en streepjes");

export const kbCategoryBodySchema = z.object({
  slug: slugSchema,
  title: z.string().trim().min(1, "Vul een titel in").max(80),
  icon: z
    .string()
    .trim()
    .regex(/^[a-z0-9-]{1,40}$/, "Naam van een Lucide-icoon, bijvoorbeeld book-open"),
  sort: z.number().int().min(0).max(999),
});
export type KbCategoryBody = z.infer<typeof kbCategoryBodySchema>;

export const KB_BODY_MAX = 100_000;
export const kbArticleBodySchema = z.object({
  categoryId: z.uuid(),
  slug: slugSchema,
  title: z.string().trim().min(1, "Vul een titel in").max(160),
  bodyMd: z.string().max(KB_BODY_MAX),
  visibility: z.enum(KB_VISIBILITIES),
  status: z.enum(PUBLISH_STATUSES),
  sort: z.number().int().min(0).max(9999).default(0),
});
export type KbArticleBody = z.infer<typeof kbArticleBodySchema>;

export interface KbCategoryDto {
  id: string;
  slug: string;
  title: string;
  icon: string;
  sort: number;
}

/** Artikel in beheer (met concepten en feedback). */
export interface KbArticleAdminRow {
  id: string;
  categoryId: string;
  slug: string;
  title: string;
  visibility: KbVisibility;
  status: PublishStatus;
  sort: number;
  updatedAt: string;
  publishedAt: string | null;
  helpfulYes: number;
  helpfulNo: number;
}
export interface KbArticleAdmin extends KbArticleAdminRow {
  bodyMd: string;
  updatedBy: string | null;
  /** Laatste toelichtingen bij "Nee" (zonder persoonsgegevens). */
  comments: { comment: string; createdAt: string }[];
}

/** Gepubliceerd artikel zoals de klant het ziet (Hulp en helpsite). */
export interface KbArticleDto {
  id: string;
  slug: string;
  title: string;
  category: { slug: string; title: string };
  html: string;
  excerpt: string;
  visibility: KbVisibility;
  updatedAt: string;
}
export interface KbSearchHit {
  slug: string;
  title: string;
  categorySlug: string;
  categoryTitle: string;
  excerpt: string;
}
export interface KbIndexResponse {
  categories: (KbCategoryDto & { articles: { slug: string; title: string; excerpt: string }[] })[];
}

export const kbFeedbackBodySchema = z.object({
  article: slugSchema,
  helpful: z.enum(["ja", "nee"]).transform((v) => v === "ja"),
  comment: z.string().trim().max(2000).optional().default(""),
});

/** Alles wat de helpsite bij het bouwen ophaalt (docs/HELPCENTER.md §10). */
export interface HelpExport {
  generatedAt: string;
  /** Status bij het bouwen; de statuspagina ververst live (docs/HELPCENTER.md §10). */
  status: StatusResponse;
  appUrl: string;
  categories: KbCategoryDto[];
  articles: KbArticleDto[];
  changelog: {
    id: string;
    date: string;
    kind: ChangelogKind;
    module: string | null;
    title: string;
    html: string;
    articleSlug: string | null;
  }[];
}

// ---------- changelog (§6) ----------

export const changelogEntryBodySchema = z.object({
  date: z.iso.date(),
  kind: z.enum(CHANGELOG_KINDS),
  module: z.string().trim().max(60).nullable().default(null),
  title: z.string().trim().min(1, "Vul een titel in").max(160),
  body: z.string().max(20_000).default(""),
  articleId: z.uuid().nullable().default(null),
  showInApp: z.boolean().default(false),
});
export type ChangelogEntryBody = z.infer<typeof changelogEntryBodySchema>;
/** Publiceren, eventueel met een mail aan de bevestigde volgers (één keer per update). */
export const changelogPublishBodySchema = z.object({ notify: z.boolean().default(false) });

export interface ChangelogEntryAdmin extends ChangelogEntryBody {
  id: string;
  status: PublishStatus;
  publishedAt: string | null;
  emailedAt: string | null;
  updatedAt: string;
}
/** Update in de app (Hulp), alleen gepubliceerd en "Ook tonen in de app". */
export interface ChangelogAppEntry {
  id: string;
  date: string;
  kind: ChangelogKind;
  module: string | null;
  title: string;
  html: string;
  articleSlug: string | null;
}
export interface ChangelogAppResponse {
  items: ChangelogAppEntry[];
  /** Er is een update van na de laatst geziene (stip bij Hulp). */
  unseen: boolean;
}

/** Link naar een artikel voor een antwoord in de inbox (§5, "Artikel invoegen"). */
export interface KbArticleLink {
  id: string;
  title: string;
  visibility: KbVisibility;
  url: string;
}
